import { useEffect, useState } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Users,
  Plus,
  Search,
  Upload,
  Download,
  Pencil,
  Trash2,
  Loader2,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    email: "",
    cedula: "",
    role: "ESTUDIANTE"
  });
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.username || !formData.password || !formData.full_name) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/users`, formData);
      toast.success("Usuario creado exitosamente");
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const updateData = { ...formData };
      if (!updateData.password) delete updateData.password;
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      toast.success("Usuario actualizado exitosamente");
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("¿Está seguro de eliminar este usuario?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      toast.error("Error al eliminar usuario");
    }
  };

  const handleDownloadTemplate = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API}/users/csv-template?format=${format}`, {
        responseType: 'blob'
      });
      const extension = format === 'xlsx' ? 'xlsx' : 'csv';
      const mimeBlob = new Blob([response.data]);
      const url = window.URL.createObjectURL(mimeBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `plantilla_usuarios.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };


  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/users/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      fetchUsers();
    } catch (error) {
      toast.error("Error al importar usuarios");
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      full_name: "",
      email: "",
      cedula: "",
      role: "ESTUDIANTE"
    });
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "",
      full_name: user.full_name,
      email: user.email || "",
      cedula: user.cedula || "",
      role: user.role
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-purple-100 text-purple-800';
      case 'PROFESOR': return 'bg-blue-100 text-blue-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 skeleton rounded" />
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">Gestión de Usuarios</h1>
          <p className="text-slate-600 mt-1">{users.length} usuarios en el sistema</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-users-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar CSV
          </Button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="role-filter">
            <SelectValue placeholder="Filtrar por rol" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los roles</SelectItem>
            <SelectItem value="ADMIN">Administrador</SelectItem>
            <SelectItem value="PROFESOR">Profesor</SelectItem>
            <SelectItem value="ESTUDIANTE">Estudiante</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Nombre Completo</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Último Acceso</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                <TableCell className="font-medium">{user.username}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>
                  <Badge className={getRoleBadgeClass(user.role)}>{user.role}</Badge>
                </TableCell>
                <TableCell>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleString() 
                    : 'Nunca'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openEditModal(user)}
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || !!editingUser} onOpenChange={() => {
        setShowCreateModal(false);
        setEditingUser(null);
        resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Usuario *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                placeholder="nombre_usuario"
                disabled={!!editingUser}
                data-testid="username-field"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                data-testid="password-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Juan Pérez"
                data-testid="fullname-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="correo@ejemplo.com"
                data-testid="email-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Número de Cédula</Label>
              <Input
                value={formData.cedula}
                onChange={(e) => setFormData(prev => ({ ...prev, cedula: e.target.value }))}
                placeholder="12345678"
                data-testid="cedula-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="role-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Administrador</SelectItem>
                  <SelectItem value="PROFESOR">Profesor</SelectItem>
                  <SelectItem value="ESTUDIANTE">Estudiante</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setEditingUser(null);
              resetForm();
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={editingUser ? handleUpdate : handleCreate}
              disabled={saving}
              data-testid="save-user-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Usuarios desde CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => handleDownloadTemplate('csv')} className="w-full" data-testid="download-template-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Descargar CSV
                </Button>
                <Button variant="outline" onClick={() => handleDownloadTemplate('xlsx')} className="w-full" data-testid="download-template-xlsx-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Descargar XLSX
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Descarga la plantilla y complétala con los datos de los usuarios.
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Las columnas deben ir en este orden: username, firstname, lastname, email y password.
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Puedes guardarla separada por comas o punto y coma o mantenerla como XLSX.
              </p>
            </div>


            <div className="border-t pt-4">
              <Label className="block mb-3">Cargar archivo CSV</Label>
              <div className="import-dropzone">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleImportFile}
                  className="hidden"
                  id="csv-upload"
                  disabled={importing}
                />

                <label htmlFor="csv-upload" className="cursor-pointer">
                  {importing ? (
                    <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  )}
                  <p className="text-slate-600">
                  {importing ? 'Importando...' : 'Clic para seleccionar archivo CSV o XLSX'}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Se aceptan archivos .csv o .xlsx con encabezados username, firstname, lastname, email y password.
                </p>
              </label>
            </div>

            </div>

            {importResult && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium">Resultado de la importación</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">{importResult.created}</p>
                    <p className="text-xs text-green-600">Creados</p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-amber-700">{importResult.duplicates}</p>
                    <p className="text-xs text-amber-600">Duplicados</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-700">{importResult.errors}</p>
                    <p className="text-xs text-red-600">Errores</p>
                  </div>
                </div>
                {importResult.error_details?.length > 0 && (
                  <div className="mt-3 text-sm">
                    <p className="font-medium text-red-600 mb-1">Errores:</p>
                    {importResult.error_details.slice(0, 5).map((err, i) => (
                      <p key={i} className="text-red-600">Fila {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportModal(false);
              setImportResult(null);
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
